import React from 'react';
import styled from 'styled-components';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import { OnboardingForm, OnboardingDone, OnboardingCall } from 'app/modules/Onboarding';
import { BASE_GENERIC_ROUTE, BASE_KAJABI_ROUTE, BASE_VHX_ROUTE, MOBILE_WIDTH, ONBOARDING_FORM_WIDTH } from '../const';

interface RewardfulAffiliate {
  affiliate: { token: string };
}

declare global {
  interface Window {
    Rewardful?: RewardfulAffiliate;
  }
}

const Background = styled.div`
  height: 100%;
  background: linear-gradient(70.3deg, #ffd874 0%, #f4845f 29.3%, #f16a75 62.39%, #f16aab 97.64%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;

  @media (max-width: ${MOBILE_WIDTH}) {
    background: white;
    @media (max-height: 850px) {
      min-height: 700px;
    }
  }
`;

const LogoWrapper = styled.div`
  position: fixed;
  top: min(60px, 3%);
  left: 120px;
  @media (max-width: 800px) {
    left: calc((100vw - ${ONBOARDING_FORM_WIDTH}) / 2); //Aligned with the form
  }
  @media (max-width: ${MOBILE_WIDTH}) {
    display: none;
  }
  @media (max-height: 900px) {
    display: none; // Hide logo on desktop views shorter than 900px
  }
`;
const MobileLogo = styled.div`
  display: none;

  @media (max-width: ${MOBILE_WIDTH}) {
    position: absolute;
    top: min(72px, 3vh);
    left: 32px;
    display: block;
  }
`;

const ROUTES = [BASE_KAJABI_ROUTE, BASE_VHX_ROUTE, BASE_GENERIC_ROUTE];

const getHelmet = () => {
  const route = window.location.pathname;
  if (route.includes('kajabi')) {
    return (
      <Helmet>
        <title>VidApp: Create Free Trial App for Kajabi</title>
        <meta
          name="description"
          content="Connect VidApp with your Kajabi to create your own branded, native apps in minutes. VidApp makes it easy to design, build and launch with all the core features your members are asking for."
        />
      </Helmet>
    );
  } else if (route.includes('vimeo')) {
    return (
      <Helmet>
        <title>VidApp: Create Free Trial App for Vimeo OTT</title>
        <meta
          name="description"
          content="Connect VidApp with your Vimeo OTT to create your own branded, native apps in minutes. Take your OTT apps to the next level with customizable layouts and premium features that your members will love you for."
        />
      </Helmet>
    );
  }
  return null;
};

export const App = () => {
  return (
    <Background>
      {getHelmet()}
      <LogoWrapper>
        <img
          src="https://files.vidapp.com/images/builder/branding/vidapp_white_small.webp"
          width={114}
          height={26}
          alt="vidapp logo"
        />
      </LogoWrapper>
      <MobileLogo>
        <img
          src="https://files.vidapp.com/images/builder/branding/vidapp_logo.webp"
          width={114}
          height={26}
          alt="vidapp logo"
        />
      </MobileLogo>
      <BrowserRouter>
        <Routes>
          {ROUTES.map((route) => (
            <>
              <Route path={route} element={<OnboardingForm />} />
              <Route path={`${route}/success`} element={<OnboardingDone redirectToBuilder />} />
              <Route
                path={`${route}/done`}
                element={
                  <OnboardingDone
                    icon={<img src="vidapp_logo.png" height={60} width={60} alt="vidapp logo" />}
                    title="Welcome and thank you for creating your account!"
                    subtitle="Be on the lookout for an email from our team to book in a demo call with you soon."
                  />
                }
              />
              <Route
                path={`${route}/mobile`}
                element={
                  <OnboardingDone
                    icon={<img src="screen.png" height={48} width={90} alt="computer screen" />}
                    title="Sign Up Successful"
                    subtitle="The VidApp Builder is best experienced on a larger screen. To continue please log into VidApp from your desktop device."
                  />
                }
              />
              <Route path={`${route}/call`} element={<OnboardingCall />} />
            </>
          ))}
        </Routes>
      </BrowserRouter>
    </Background>
  );
};
